/**
 * ApsDrawingViewer — drop-in replacement for the legacy SVG viewer
 * (`dangerouslySetInnerHTML`) used by PFD / P&ID / SLD document viewers.
 *
 * Renders an Autodesk Platform Services (APS) Viewer (a.k.a. Forge
 * Viewer v7) showing the SVF2 derivative of an engineering drawing
 * that was SVG-rendered server-side and translated via
 * /api/projects/:id/aps/drawings/:sheet_id/translate.
 *
 * Lifecycle (matches the user's chosen UX in the planning step):
 *   1. Idle           — sheet has no APS derivative yet. Show
 *                       "Generate APS view" button.
 *   2. Translating    — POST translate, poll manifest every 3 s.
 *   3. Ready          — manifest.success → load SVF2 in the APS Viewer.
 *   4. Failed         — show error + Retry.
 *
 * Editors are untouched; editor saves only update the SVG. The user
 * clicks "Refresh APS view" (or "Generate" the first time) to
 * re-translate after edits.
 *
 * Props
 *   - projectId   {string}  required
 *   - sheetId     {string}  required (e.g. "PID-001", "PFD-001")
 *   - title       {string}  optional — header label
 *   - docKind     {string}  optional — "pfd" | "pid" | "sld"
 *   - svgHash     {string}  optional — when this changes (editor saved
 *                                       a new revision) the viewer
 *                                       offers a "Refresh" CTA.
 *   - onExit      {func}    optional — invoked when the user wants to
 *                                       return to the editor (used by
 *                                       the document viewers).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { useTheme } from "../../lib/theme";
import {
  Loader2, RefreshCcw, AlertCircle, Play, Box, ExternalLink,
} from "lucide-react";

const POLL_MS = 3000;

export default function ApsDrawingViewer({
  projectId, sheetId, title, docKind, svgHash, onExit,
}) {
  const { theme } = useTheme();
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [record, setRecord] = useState(null);      // server record { urn, status, content_hash }
  const [phase, setPhase] = useState("loading");   // loading | idle | translating | ready | failed
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState("");
  const pollTimerRef = useRef(null);

  // ── Load existing manifest record on mount / sheet switch ──
  const loadManifest = useCallback(async () => {
    if (!projectId || !sheetId) return;
    try {
      const r = await api.get(
        `/projects/${projectId}/aps/drawings/${sheetId}/manifest`,
        { params: { theme } });
      if (!r.data || r.data.exists === false) {
        setRecord(null);
        setPhase("idle");
        return null;
      }
      const ms = r.data.manifest_status || r.data.status;
      setRecord(r.data);
      setProgress(r.data.progress || "");
      if (ms === "success") setPhase("ready");
      else if (ms === "failed") {
        setPhase("failed");
        setErr(r.data.error || "Translation failed");
      } else {
        setPhase("translating");
      }
      return r.data;
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
      setPhase("failed");
      return null;
    }
  }, [projectId, sheetId, theme]);

  useEffect(() => {
    // Defer to next tick so React doesn't flag the setState chain
    // inside an in-flight effect (react-hooks/set-state-in-effect).
    const loadTimer = setTimeout(() => {
      setRecord(null);
      setErr("");
      setPhase("loading");
      loadManifest();
    }, 0);
    return () => {
      clearTimeout(loadTimer);
      stopPolling();
      destroyViewer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, sheetId, theme]);

  // ── Trigger a (re)translation ──
  const startTranslation = useCallback(async (force = false) => {
    setErr("");
    setPhase("translating");
    setProgress("Submitting to APS Model Derivative…");
    try {
      const r = await api.post(
        `/projects/${projectId}/aps/drawings/${sheetId}/translate`,
        { force, doc_kind: docKind, theme });
      setRecord(r.data);
      // Immediately poll once to pick up "success" if the translation
      // was already cached / completed quickly.
      pollOnce();
    } catch (e) {
      const msg = formatErr(e?.response?.data?.detail) || e.message;
      setErr(msg);
      setPhase("failed");
    }
  }, [projectId, sheetId, docKind, theme]); // eslint-disable-line

  // ── Manifest polling ──
  const pollOnce = useCallback(async () => {
    const r = await loadManifest();
    if (r && (r.manifest_status === "success"
              || r.manifest_status === "failed"
              || r.status === "success" || r.status === "failed")) {
      stopPolling();
    }
  }, [loadManifest]);

  useEffect(() => {
    if (phase === "translating") {
      stopPolling();
      pollTimerRef.current = setInterval(pollOnce, POLL_MS);
    } else {
      stopPolling();
    }
    return stopPolling;
  }, [phase, pollOnce]);

  function stopPolling() {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  // ── APS Viewer lifecycle ──
  function destroyViewer() {
    try {
      if (viewerRef.current) {
        viewerRef.current.finish();
        viewerRef.current = null;
      }
    } catch { /* */ }
  }

  // Boot viewer once we are in the "ready" phase & DOM is mounted.
  useEffect(() => {
    if (phase !== "ready" || !record?.urn || !containerRef.current) return;
    const Autodesk = window.Autodesk;
    if (!Autodesk || !Autodesk.Viewing) {
      // Defer the state updates to next tick so React doesn't flag
      // the setState chain inside an in-flight effect.
      const t = setTimeout(() => {
        setErr("Autodesk Viewer SDK failed to load (network blocked?).");
        setPhase("failed");
      }, 0);
      return () => clearTimeout(t);
    }

    let canceled = false;
    const getAccessToken = async (onTokenReady) => {
      try {
        const r = await api.get("/aps/viewer-token");
        onTokenReady(r.data.access_token, r.data.expires_in || 3600);
      } catch (e) {
        setErr("Failed to fetch APS viewer token: " + (e.message || e));
        setPhase("failed");
      }
    };

    Autodesk.Viewing.Initializer({ env: "AutodeskProduction2",
                                    api: "streamingV2",
                                    getAccessToken }, () => {
      if (canceled) return;
      destroyViewer();
      const v = new Autodesk.Viewing.GuiViewer3D(containerRef.current, {
        extensions: [],
      });
      const started = v.start();
      if (started > 0) {
        setErr("APS Viewer failed to start (code " + started + ").");
        setPhase("failed");
        return;
      }
      viewerRef.current = v;

      const documentId = `urn:${record.urn}`;
      const wants3d = (docKind === "plant3d" ||
                         docKind === "model3d" ||
                         docKind === "3d");
      Autodesk.Viewing.Document.load(documentId, (doc) => {
        if (canceled) return;
        // Pick the appropriate viewable. For 3D documents (OBJ/IFC
        // sources) APS only emits a 3D viewable — so we prefer the
        // 3D one when ``docKind`` requests it, otherwise fall back
        // to the first 2D page.
        const root = doc.getRoot();
        const geometries = root.search({ type: "geometry" }) || [];
        const node = wants3d
          ? (geometries.find((g) => g.data?.role === "3d")
              || geometries.find((g) => g.is3D?.())
              || geometries[0])
          : (geometries.find((g) => g.data?.role === "2d")
              || geometries.find((g) => g.is2D?.())
              || geometries[0]);
        if (!node) {
          setErr("APS document has no viewable.");
          setPhase("failed");
          return;
        }
        v.loadDocumentNode(doc, node).then(() => {
          if (wants3d) {
            // 3D path — apply a sensible isometric default camera
            // and modern lighting. ``setBackgroundColor`` controls
            // the sky/ground gradient (4-component) for 3D scenes.
            const bg = theme === "light"
                ? { top: [245, 247, 250], bot: [220, 225, 234] }
                : { top: [22, 28, 42],    bot: [8, 12, 22] };
            try {
              v.setBackgroundColor(
                bg.top[0], bg.top[1], bg.top[2],
                bg.bot[0], bg.bot[1], bg.bot[2]);
            } catch { /* noop */ }
            try {
              // Modern PBR scene quality preset for glTF-derived SVF2.
              v.setLightPreset(13);  // "Studio Soft Light" — matches PBR
              v.setQualityLevel(true, true);   // SSAO + smooth-line
              v.setGhosting(true);
              v.setGroundShadow(true);
              v.setGroundReflection(false);
              v.setEnvMapBackground?.(false);
              // Enable shadows + ambient occlusion post-processing.
              v.prefs?.set?.("ambientShadows", true);
              v.prefs?.set?.("antialiasing", true);
              v.prefs?.set?.("groundShadow", true);
            } catch { /* noop */ }
            // Wait for model geometry to finish loading before
            // framing the camera — otherwise getBoundingBox returns
            // an empty/zero box and fitToView frames into space.
            const applyIsoCamera = () => {
              try {
                v.navigation?.toPerspective?.();
                const cam = v.impl?.camera;
                const bbox = v.model?.getBoundingBox?.();
                if (cam && bbox && !bbox.isEmpty?.()) {
                  // Use the existing cam.position vector as a fresh
                  // template (cloning preserves the right THREE.js
                  // constructor without relying on Autodesk.Private).
                  const ctr  = cam.position.clone();
                  const sz   = cam.position.clone();
                  bbox.getCenter(ctr);
                  bbox.getSize(sz);
                  // Distance just outside the bbox sphere — APS will
                  // then re-frame tightly with fitToView() below.
                  const dist = Math.max(sz.x, sz.y, sz.z) * 1.2;
                  // SW-elevated isometric view.
                  cam.position.set(
                    ctr.x - dist * 0.95,
                    ctr.y + dist * 0.75,
                    ctr.z + dist * 0.95);
                  cam.up.set(0, 1, 0);
                  if (cam.target?.copy) cam.target.copy(ctr);
                  else cam.target = ctr.clone();
                  cam.lookAt(ctr);
                  cam.updateMatrix?.();
                  cam.updateProjectionMatrix?.();
                  v.impl.syncCamera?.(true);
                  v.impl.invalidate?.(true, true, true);
                  // Frame the model tightly *after* iso orientation is
                  // committed so equipment fills the viewport.
                  setTimeout(() => {
                    try { v.fitToView(); } catch { /* noop */ }
                  }, 120);
                } else {
                  v.fitToView();
                }
              } catch { /* noop */ }
            };
            const onGeomLoad = () => {
              try {
                v.removeEventListener(
                  window.Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
                  onGeomLoad);
              } catch { /* noop */ }
              setTimeout(applyIsoCamera, 80);
            };
            try {
              v.addEventListener(
                window.Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
                onGeomLoad);
              // Fallback in case the event already fired.
              setTimeout(applyIsoCamera, 1800);
            } catch { /* noop */ }
          } else {
            // 2D path — preserve previous behaviour for plot-plan /
            // P&ID / SLD viewers.
            const bg = theme === "light"
                ? { rgb: [248, 249, 251], page: [248/255, 249/255, 251/255, 1] }
                : { rgb: [10, 14, 26],    page: [10/255, 14/255, 26/255, 1] };
            try { v.setBackgroundColor(bg.rgb[0], bg.rgb[1], bg.rgb[2],
                                        bg.rgb[0], bg.rgb[1], bg.rgb[2]); } catch { /* noop */ }
            try {
              const data = v.model?.getData?.();
              if (data) {
                data.pageColor = bg.page;
                data.bgColor = bg.page;
              }
            } catch { /* noop */ }
            try {
              const rdr = v.impl?.glrenderer?.();
              rdr?.setClearColor?.((bg.rgb[0] << 16) | (bg.rgb[1] << 8)
                                    | bg.rgb[2], 1);
            } catch { /* noop */ }
            try { v.impl?.invalidate?.(true, true, true); } catch { /* noop */ }
            try {
              v.fitToView();
              v.setLightPreset(0);
              v.setQualityLevel(false, true);
            } catch { /* noop */ }
          }
        }).catch((e) => {
          setErr("loadDocumentNode failed: " + (e?.message || e));
          setPhase("failed");
        });
      }, (errCode, errMsg) => {
        setErr(`Document.load failed: ${errCode} ${errMsg || ""}`);
        setPhase("failed");
      });
    });

    return () => { canceled = true; destroyViewer(); };
  }, [phase, record?.urn, theme]); // eslint-disable-line

  // ── Detect editor changes via server-computed drift flag ──
  const drift = !!record?.drift;

  // ── Render ──
  if (phase === "loading") {
    return (
      <PaneShell title={title} onExit={onExit}>
        <Center>
          <Loader2 size={20} className="animate-spin"
              style={{ color: "var(--pf-spark, #22d3ee)" }} />
          <div style={lbl}>Checking APS translation status…</div>
        </Center>
      </PaneShell>
    );
  }

  if (phase === "idle") {
    return (
      <PaneShell title={title} onExit={onExit}>
        <Center>
          <Box size={28} style={{ color: "var(--pf-spark, #22d3ee)",
              opacity: 0.75 }} />
          <div style={{ ...lbl, fontSize: 14, fontWeight: 600 }}>
            Drawing not yet sent to Autodesk Forge Viewer
          </div>
          <div style={{ ...lbl, maxWidth: 520, lineHeight: 1.55 }}>
            Click below to translate this sheet&apos;s latest SVG into an
            Autodesk SVF2 derivative. The viewer keeps the same drawing —
            but with native pan / zoom / measure / explode / markup
            tools.
          </div>
          <PrimaryBtn onClick={() => startTranslation(false)}
              dataTestId="aps-generate-btn">
            <Play size={13}/> Generate APS view
          </PrimaryBtn>
        </Center>
      </PaneShell>
    );
  }

  if (phase === "translating") {
    return (
      <PaneShell title={title} onExit={onExit}>
        <Center>
          <Loader2 size={28} className="animate-spin"
              style={{ color: "var(--pf-spark, #22d3ee)" }} />
          <div style={{ ...lbl, fontSize: 13, fontWeight: 600 }}>
            Translating to Autodesk SVF2…
          </div>
          <div style={{ ...lbl, color: "var(--pf-text-muted, #94a3b8)" }}>
            {progress
              ? `Progress: ${progress}`
              : "Polling APS Model Derivative every 3 seconds…"}
          </div>
        </Center>
      </PaneShell>
    );
  }

  if (phase === "failed") {
    return (
      <PaneShell title={title} onExit={onExit}>
        <Center>
          <AlertCircle size={26} style={{ color: "#f87171" }} />
          <div style={{ ...lbl, fontSize: 13, fontWeight: 600,
                color: "#fecaca" }}>
            APS Viewer error
          </div>
          <div style={{ ...lbl, maxWidth: 600 }}>{err || "Unknown error"}</div>
          <PrimaryBtn onClick={() => startTranslation(true)}
              dataTestId="aps-retry-btn">
            <RefreshCcw size={13}/> Retry
          </PrimaryBtn>
        </Center>
      </PaneShell>
    );
  }

  // phase === "ready"
  return (
    <PaneShell title={title} onExit={onExit}
      headerRight={
        <>
          {drift && (
            <span data-testid="aps-drift-indicator"
              style={{ background: "rgba(234, 179, 8, 0.15)",
                color: "#facc15", border: "1px solid #ca8a04",
                padding: "3px 8px", borderRadius: 999,
                fontSize: 10, fontFamily: "'JetBrains Mono', monospace",
                marginRight: 8 }}>
              Editor revised — refresh recommended
            </span>
          )}
          <button data-testid="aps-refresh-btn"
              onClick={() => startTranslation(true)}
              title="Re-translate the latest SVG from the editor"
              style={ghostBtn}>
            <RefreshCcw size={12} /> Refresh APS view
          </button>
        </>
      }>
      <div ref={containerRef} data-testid="aps-viewer-canvas"
          style={{ position: "absolute", inset: 0,
              background: "var(--pf-bg, #020617)" }} />
    </PaneShell>
  );
}

// ── Layout helpers ──
function PaneShell({ title, headerRight, onExit, children }) {
  return (
    <div data-testid="aps-drawing-viewer"
      style={{ position: "relative", flex: 1, minHeight: 0,
          display: "flex", flexDirection: "column",
          background: "var(--pf-bg, #020617)",
          color: "var(--pf-text, #e2e8f0)",
          fontFamily: "'JetBrains Mono', monospace" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10,
            padding: "8px 14px",
            borderBottom: "1px solid var(--pf-border, #1e293b)",
            background: "var(--pf-surface, #0f1729)",
            fontSize: 12 }}>
        <ExternalLink size={13}
          style={{ color: "var(--pf-spark, #22d3ee)" }}/>
        <span style={{ fontWeight: 600 }}>Autodesk Forge Viewer</span>
        {title && (
          <span style={{ color: "var(--pf-text-muted, #94a3b8)",
              marginLeft: 6, fontSize: 11 }}>
            · {title}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {headerRight}
        {onExit && (
          <button data-testid="aps-exit-btn" onClick={onExit}
            title="Return" style={{ ...ghostBtn, marginLeft: 6 }}>
            Close
          </button>
        )}
      </div>
      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        {children}
      </div>
    </div>
  );
}

function Center({ children }) {
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex",
          flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: 12, textAlign: "center",
          padding: 24 }}>
      {children}
    </div>
  );
}

const lbl = {
  fontSize: 12,
  color: "var(--pf-text, #e2e8f0)",
  fontFamily: "'JetBrains Mono', monospace",
};

const ghostBtn = {
  background: "transparent",
  border: "1px solid var(--pf-border, #1e293b)",
  color: "var(--pf-text, #e2e8f0)",
  padding: "5px 10px",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: 11,
  fontFamily: "'JetBrains Mono', monospace",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
};

function PrimaryBtn({ children, onClick, dataTestId }) {
  return (
    <button data-testid={dataTestId} onClick={onClick}
      style={{ background: "var(--pf-spark, #22d3ee)",
          color: "#020617", border: "none",
          padding: "8px 16px", borderRadius: 4,
          cursor: "pointer", fontWeight: 600,
          fontSize: 12,
          fontFamily: "'JetBrains Mono', monospace",
          display: "inline-flex", alignItems: "center", gap: 6 }}>
      {children}
    </button>
  );
}
